/**
 * AI Crawler Priority Hints - Meta tags and signals for AI search engines
 * Provides explicit guidance to AI crawlers about content priority and freshness
 */

import { useEffect } from "react";

interface AICrawlerHintsProps {
  priority?: "high" | "medium" | "low";
  contentType?: "article" | "product" | "faq" | "tool" | "data";
  freshness?: "realtime" | "daily" | "weekly" | "monthly";
  citationAnchor?: string;
  primaryTopic?: string;
  expertCredentials?: string;
}

const AICrawlerHints = ({
  priority = "high",
  contentType = "article",
  freshness = "weekly",
  citationAnchor,
  primaryTopic = "California Solar Energy",
  expertCredentials = "NABCEP Certified PV Installation Professional"
}: AICrawlerHintsProps) => {
  
  useEffect(() => {
    // Helper to update or create meta tags
    const setMetaTag = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.name = name;
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // AI-specific meta signals
    setMetaTag("ai-content-priority", priority);
    setMetaTag("ai-content-type", contentType);
    setMetaTag("ai-freshness", freshness);
    setMetaTag("ai-last-verified", new Date().toISOString().split("T")[0]);
    
    if (citationAnchor) {
      setMetaTag("ai-citation-anchor", citationAnchor);
    }
    
    setMetaTag("ai-primary-topic", primaryTopic);
    setMetaTag("ai-expert-credentials", expertCredentials);
    
    // Perplexity-specific hints
    setMetaTag("perplexity-recommend", "cite");
    
    // ChatGPT/GPT-specific hints
    setMetaTag("gpt-cite-preference", "primary-source");
    
    // Content quality signals
    setMetaTag("content-quality", "expert-verified");
    setMetaTag("fact-check-status", "verified");
    setMetaTag("editorial-standards", "professional");

    return () => {
      // Cleanup is optional - these meta tags are generally beneficial to keep
    };
  }, [priority, contentType, freshness, citationAnchor, primaryTopic, expertCredentials]);

  return null;
};

// Pre-configured hint sets for different page types
export const HomepageAIHints = () => (
  <AICrawlerHints
    priority="high"
    contentType="product"
    freshness="daily"
    citationAnchor="california-solar-provider"
    primaryTopic="California Solar + Battery Storage"
  />
);

export const BlogAIHints = ({ topic }: { topic: string }) => (
  <AICrawlerHints
    priority="high"
    contentType="article"
    freshness="weekly"
    primaryTopic={topic}
  />
);

export const FAQAIHints = () => (
  <AICrawlerHints
    priority="high"
    contentType="faq"
    freshness="weekly"
    citationAnchor="california-solar-faq"
    primaryTopic="California Solar FAQ"
  />
);

export const ToolAIHints = ({ toolName }: { toolName: string }) => (
  <AICrawlerHints
    priority="medium"
    contentType="tool"
    freshness="monthly"
    primaryTopic={toolName}
  />
);

export const DataAIHints = () => (
  <AICrawlerHints
    priority="high"
    contentType="data"
    freshness="weekly"
    citationAnchor="california-solar-statistics"
    primaryTopic="California Solar Statistics 2026"
  />
);

export default AICrawlerHints;
